import type { ExportArchive, ExportResult } from '@/core/export/types'

export const DOWNLOAD_URL_LIFETIME_MS = 10_000
export const MULTI_DOWNLOAD_GAP_MS = 150

function triggerDownload(blob: Blob, fileName: string): void {
  if (!fileName.trim()) throw new Error('Download file name must not be empty')
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.hidden = true
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_LIFETIME_MS)
}

function waitForNextDownload(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, MULTI_DOWNLOAD_GAP_MS))
}

export function download(results: readonly ExportResult[]): void {
  if (results.length !== 1) throw new Error('Single-file download requires exactly one export result')
  const result = results[0]
  if (!result) throw new Error('Download result is missing')
  triggerDownload(result.blob, result.fileName)
}

export async function downloadAll(results: readonly ExportResult[]): Promise<void> {
  if (results.length === 0) throw new Error('Multi-file download requires at least one export result')
  for (let index = 0; index < results.length; index += 1) {
    const result = results[index]
    if (!result) throw new Error(`Download result ${index + 1} is missing`)
    triggerDownload(result.blob, result.fileName)
    if (index < results.length - 1) await waitForNextDownload()
  }
}

export function downloadArchive(archive: ExportArchive): void {
  if (archive.blob.type !== 'application/zip') {
    throw new Error(`Archive download requires application/zip, received "${archive.blob.type || 'unknown'}"`)
  }
  triggerDownload(archive.blob, archive.fileName)
}
